// POSModal.js - Kassahantering för försäljning direkt från patientvyn
import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../UserContext';
import { getProducts, createSale, getSaleReceiptPdf, getAppointmentTypes, getLinkableAppointments } from '../Calendar/calendarApi';
import './POSModal.css';

const PAYMENT_METHODS = [
  { value: 'card', label: 'Kort', icon: 'fa-credit-card' },
  { value: 'cash', label: 'Kontant', icon: 'fa-money-bill-wave' },
  { value: 'swish', label: 'Swish', icon: 'fa-mobile-alt' },
  { value: 'frikort', label: 'Frikort', icon: 'fa-id-card' }
  // OBS: 'uteblivet' medvetet INTE här - en produktförsäljning kan inte utebli,
  // och metoden skulle dra beloppet ur dagskassans total (backend avvisar den också)
];

const POSModal = ({ patient, onClose, onSaleComplete }) => {
  const { user } = useContext(UserContext);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Katalogflik: produkter eller besök (bokningstyper)
  const [catalogTab, setCatalogTab] = useState('products');
  const [appointmentTypes, setAppointmentTypes] = useState([]);

  // Bokning att koppla ett besök till (valfritt)
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState('');

  // Varukorg
  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  // Ladda produkter, bokningstyper och patientens bokningar
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [prod, types] = await Promise.all([getProducts(true), getAppointmentTypes()]);
        setProducts(prod);
        setAppointmentTypes(types || []);
      } catch (err) {
        setError('Kunde inte ladda kassadata');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadAppointments = async () => {
      if (!patient?.patient_id) return;
      try {
        const data = await getLinkableAppointments(patient.patient_id);
        setPatientAppointments(data || []);
      } catch (err) {
        // Icke-kritiskt - koppling blir bara otillgänglig
      }
    };
    loadAppointments();
  }, [patient?.patient_id]);

  // Filtrera produkter baserat på sökterm
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Filtrera besökstyper baserat på sökterm
  const filteredVisitTypes = appointmentTypes.filter(type =>
    type.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Lägg till produkt i varukorgen
  const addToCart = (product) => {
    const cartKey = `p${product.product_id}`;
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.cartKey === cartKey);
      if (existingItem) {
        return prevCart.map(item =>
          item.cartKey === cartKey ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prevCart, { ...product, cartKey, quantity: 1 }];
    });
  };

  // Lägg till ett besök (bokningstyp) i varukorgen. Vård är momsbefriad -> 0 %.
  const addVisitToCart = (type) => {
    const cartKey = `v${type.type_id}`;
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.cartKey === cartKey);
      if (existingItem) {
        return prevCart.map(item =>
          item.cartKey === cartKey ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prevCart, {
        cartKey,
        isVisit: true,
        type_id: type.type_id,
        name: type.name,
        price: type.default_price || 0,
        vat_rate: 0,
        is_friskvard: false,
        quantity: 1,
      }];
    });
  };

  // Uppdatera kvantitet
  const updateQuantity = (cartKey, newQuantity) => {
    if (newQuantity <= 0) {
      setCart(prevCart => prevCart.filter(item => item.cartKey !== cartKey));
    } else {
      setCart(prevCart =>
        prevCart.map(item =>
          item.cartKey === cartKey ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  };

  // Ta bort från varukorg
  const removeFromCart = (cartKey) => {
    setCart(prevCart => prevCart.filter(item => item.cartKey !== cartKey));
  };

  // Finns ett besök i varukorgen? (styr om koppling-valet visas)
  const cartHasVisit = cart.some(item => item.isVisit);

  // Beräkna totaler
  const calculateTotals = () => {
    let total = 0;
    let totalVat = 0;
    let hasFriskvard = false;

    cart.forEach(item => {
      const lineTotal = item.price * item.quantity;
      const lineVat = lineTotal - (lineTotal / (1 + item.vat_rate / 100));
      total += lineTotal;
      totalVat += lineVat;
      if (item.is_friskvard) {
        hasFriskvard = true;
      }
    });

    return { total, totalVat, hasFriskvard };
  };

  const { total, totalVat, hasFriskvard } = calculateTotals();

  // Genomför köp
  const handleSubmit = async () => {
    if (cart.length === 0) {
      setError('Lägg till minst en produkt eller ett besök');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const saleData = {
        patient_id: patient.patient_id,
        items: cart.map(item => (
          item.isVisit
            ? {
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                vat_rate: item.vat_rate,
                is_friskvard: item.is_friskvard,
              }
            : {
                product_id: item.product_id,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                vat_rate: item.vat_rate,
                is_friskvard: item.is_friskvard,
              }
        )),
        notes: notes,
        payment: {
          payment_method: paymentMethod,
          amount: total,
          appointment_id: (cartHasVisit && selectedAppointmentId) ? parseInt(selectedAppointmentId, 10) : null,
        }
      };

      const result = await createSale(saleData);

      // Hämta kvitto-PDF
      if (result.sale_id) {
        await getSaleReceiptPdf(result.sale_id);
      }

      if (onSaleComplete) {
        onSaleComplete(result);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte genomföra köp');
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="pos-modal-overlay" onClick={onClose}>
      <div className="pos-modal" onClick={e => e.stopPropagation()}>
        <div className="pos-modal-header">
          <h2><i className="fas fa-cash-register"></i> Kassa</h2>
          <div className="pos-patient-info">
            <span className="patient-name">{patient.firstname} {patient.lastname}</span>
            <span className="patient-pnr">{patient.personnummer}</span>
          </div>
          <button className="pos-close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="pos-modal-body">
          {error && <div className="pos-error">{error}</div>}

          <div className="pos-layout">
            {/* Vänster: Produktväljare */}
            <div className="pos-products-section">
              <div className="pos-catalog-tabs">
                <button
                  className={catalogTab === 'products' ? 'active' : ''}
                  onClick={() => setCatalogTab('products')}
                >
                  <i className="fas fa-box"></i> Produkter
                </button>
                <button
                  className={catalogTab === 'visits' ? 'active' : ''}
                  onClick={() => setCatalogTab('visits')}
                >
                  <i className="fas fa-user-md"></i> Besök
                </button>
              </div>

              <div className="pos-search">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder={catalogTab === 'products' ? 'Sök produkt...' : 'Sök besökstyp...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {loading ? (
                <div className="pos-loading">
                  <i className="fas fa-spinner fa-spin"></i> Laddar...
                </div>
              ) : catalogTab === 'products' ? (
                filteredProducts.length === 0 ? (
                  <div className="pos-empty">
                    <i className="fas fa-box-open"></i>
                    <p>{searchTerm ? 'Inga produkter matchar sökningen' : 'Inga produkter tillgängliga'}</p>
                  </div>
                ) : (
                  <div className="pos-products-grid">
                    {filteredProducts.map(product => (
                      <button
                        key={product.product_id}
                        className={`pos-product-card ${product.is_friskvard ? 'friskvard' : ''}`}
                        onClick={() => addToCart(product)}
                      >
                        <div className="product-card-name">{product.name}</div>
                        <div className="product-card-price">{product.price.toFixed(2)} kr</div>
                        {product.is_friskvard && (
                          <span className="friskvard-badge">
                            <i className="fas fa-heart"></i> Friskvård
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )
              ) : filteredVisitTypes.length === 0 ? (
                <div className="pos-empty">
                  <i className="fas fa-user-md"></i>
                  <p>{searchTerm ? 'Inga besökstyper matchar sökningen' : 'Inga besökstyper tillgängliga'}</p>
                </div>
              ) : (
                <div className="pos-products-grid">
                  {filteredVisitTypes.map(type => (
                    <button
                      key={type.type_id}
                      className="pos-product-card pos-visit-card"
                      onClick={() => addVisitToCart(type)}
                    >
                      <div className="product-card-name">{type.name}</div>
                      <div className="product-card-price">{(type.default_price || 0).toFixed(2)} kr</div>
                      <span className="visit-badge"><i className="fas fa-user-md"></i> Besök</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Höger: Varukorg */}
            <div className="pos-cart-section">
              <div className="pos-cart-header">
                <h3><i className="fas fa-shopping-cart"></i> Varukorg</h3>
                {cart.length > 0 && (
                  <button
                    className="clear-cart-btn"
                    onClick={() => setCart([])}
                  >
                    Töm
                  </button>
                )}
              </div>

              {cart.length === 0 ? (
                <div className="pos-cart-empty">
                  <i className="fas fa-shopping-basket"></i>
                  <p>Varukorgen är tom</p>
                </div>
              ) : (
                <div className="pos-cart-items">
                  {cart.map(item => (
                    <div key={item.cartKey} className="pos-cart-item">
                      <div className="cart-item-info">
                        <span className="cart-item-name">
                          {item.name}
                          {item.isVisit && <i className="fas fa-user-md visit-icon" title="Besök"></i>}
                          {item.is_friskvard && <i className="fas fa-heart friskvard-icon"></i>}
                        </span>
                        <span className="cart-item-price">{item.price.toFixed(2)} kr</span>
                      </div>
                      <div className="cart-item-controls">
                        <button
                          className="qty-btn"
                          onClick={() => updateQuantity(item.cartKey, item.quantity - 1)}
                        >
                          <i className="fas fa-minus"></i>
                        </button>
                        <span className="qty-value">{item.quantity}</span>
                        <button
                          className="qty-btn"
                          onClick={() => updateQuantity(item.cartKey, item.quantity + 1)}
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                        <button
                          className="remove-btn"
                          onClick={() => removeFromCart(item.cartKey)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                      <div className="cart-item-total">
                        {(item.price * item.quantity).toFixed(2)} kr
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Friskvårdsindikator */}
              {hasFriskvard && (
                <div className="friskvard-notice">
                  <i className="fas fa-heart"></i>
                  <span>Friskvårdsprodukter inkluderade - kvitto visar personnummer</span>
                </div>
              )}

              {/* Totaler */}
              <div className="pos-totals">
                <div className="total-row vat">
                  <span>Varav moms:</span>
                  <span>{totalVat.toFixed(2)} kr</span>
                </div>
                <div className="total-row grand-total">
                  <span>Totalt:</span>
                  <span>{total.toFixed(2)} kr</span>
                </div>
              </div>

              {/* Koppla besök till en bokning (valfritt) */}
              {cartHasVisit && (
                <div className="pos-link-appointment">
                  <label><i className="fas fa-link"></i> Koppla besök till bokning (valfritt)</label>
                  <select
                    value={selectedAppointmentId}
                    onChange={(e) => setSelectedAppointmentId(e.target.value)}
                  >
                    <option value="">Ingen koppling – lös betalning</option>
                    {patientAppointments.map((a) => (
                      <option key={a.appointment_id} value={a.appointment_id}>
                        {a.start_time ? new Date(a.start_time).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        {a.appointment_type ? ` · ${a.appointment_type.name}` : ''}
                        {a.is_paid ? ' (redan betald)' : ''}
                      </option>
                    ))}
                  </select>
                  {patientAppointments.length === 0 && (
                    <span className="pos-link-hint">Patienten har inga bokningar att koppla till – betalningen blir lös och kan kopplas från startsidan.</span>
                  )}
                </div>
              )}

              {/* Betalningsmetod */}
              <div className="pos-payment-method">
                <label>Betalningsmetod:</label>
                <div className="payment-options">
                  {PAYMENT_METHODS.map(method => (
                    <button
                      key={method.value}
                      className={`payment-option ${paymentMethod === method.value ? 'active' : ''}`}
                      onClick={() => setPaymentMethod(method.value)}
                    >
                      <i className={`fas ${method.icon}`}></i>
                      <span>{method.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Anteckning */}
              <div className="pos-notes">
                <label>Anteckning (valfritt):</label>
                <input
                  type="text"
                  placeholder="T.ex. rabatt, kampanj..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="pos-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Avbryt
          </button>
          <button
            className="btn btn-primary btn-pay"
            onClick={handleSubmit}
            disabled={processing || cart.length === 0}
          >
            {processing ? (
              <><i className="fas fa-spinner fa-spin"></i> Behandlar...</>
            ) : (
              <><i className="fas fa-check"></i> Slutför köp ({total.toFixed(2)} kr)</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default POSModal;
